
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Ticket } from '@/types/crm';

export const useTickets = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchTickets = async () => {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .select(`
          *,
          organization:organizations(name, sector),
          contact:contacts(first_name, last_name, email),
          project:projects(name)
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTickets(data as any);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast({
        title: 'Fout bij laden tickets',
        description: 'Er ging iets mis bij het ophalen van de tickets.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const createTicket = async (ticketData: any) => {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .insert(ticketData)
        .select()
        .single();

      if (error) throw error;

      setTickets(prev => [data as any, ...prev]);
      toast({
        title: 'Ticket aangemaakt',
        description: 'Het ticket is succesvol aangemaakt.',
      });

      return data;
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast({
        title: 'Fout bij aanmaken ticket',
        description: 'Er ging iets mis bij het aanmaken van het ticket.',
        variant: 'destructive'
      });
      throw error;
    }
  };

  const updateTicket = async (id: string, updates: any) => {
    try {
      const { data, error } = await supabase
        .from('tickets')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setTickets(prev => prev.map(ticket => 
        ticket.id === id ? { ...ticket, ...data } : ticket
      ));

      toast({
        title: 'Ticket bijgewerkt',
        description: 'Het ticket is succesvol bijgewerkt.',
      });
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast({
        title: 'Fout bij bijwerken ticket',
        description: 'Er ging iets mis bij het bijwerken van het ticket.',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  return {
    tickets,
    loading,
    refetch: fetchTickets,
    createTicket,
    updateTicket
  };
};
